import { MenuItemOrder } from '@lsy-netline/netline-ui';
import clsx from 'clsx';
import { uniqueId } from 'lodash-es';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useModules } from 'hooks';
import useFullscreen from 'hooks/useFullscreen';
import {
  IconButtonProps,
  MenuItemProps,
  Sidebar,
  SidebarMenuItemEntity,
  SidebarOpenHandler,
  sidebarMenuItemClasses,
  styled,
  useTheme,
} from 'netline-ui';
import services from 'services';
import { getMainConfig, logger } from 'utils';
import getAppFrameTheme from 'utils/theme';

import { SIDEBAR_ITEM_ORDER_NAMESPACE } from '../../../consts';
import Divider from '../Divider/Divider';

import AnchorContextMenu, { ContextMenuOptions } from './AnchorContextMenu';
import styles, { sidebarNavigationClasses } from './SidebarNavigation.styles';
import { SavedSidebarItem, SidebarNavigationProps } from './SidebarNavigation.types';
import {
  findParentWithClass,
  findSVGElement,
  groupModules,
  isSidebarPinningDisabled,
  normalizeModules,
  sortSidebarItems,
} from './SidebarNavigation.utils';

const SidebarNavigationRoot = styled('div', { name: sidebarNavigationClasses.$name })(styles);

const defaultContextValue: ContextMenuOptions = { to: '', anchorEl: null };

/**
 * Sidebar uses only hash route navigation so turn off `activeId` handling by
 * returning false in the click handler.
 */
const handleMenuItemClick = () => false;

const saveReorderToStorage = async (menuItemOrder: MenuItemOrder) => {
  try {
    await services.appFrame.userStorage.setItem(SIDEBAR_ITEM_ORDER_NAMESPACE, menuItemOrder, { force: true });
  } catch (ex: unknown) {
    logger.warn('Error saving sidebar item order:', ex);
  }
};

/**
 * Renders left side navigation panel.
 */
const SidebarNavigation = ({ className, submenuContainerRef }: SidebarNavigationProps) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState(defaultContextValue);
  const {
    i18n: { exists },
    t,
  } = useTranslation();
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(defaultContextValue);
  }, []);
  const allowReorder = getMainConfig('allowSidebarReorder');

  const handleRightClick = useCallback(
    (event, route) => {
      event.preventDefault();
      const properTarget = menuOpen
        ? findParentWithClass(event.target, sidebarMenuItemClasses.root)
        : findSVGElement(event.target);
      setContextMenu({
        anchorEl: properTarget as Element,
        to: route,
      });
    },
    [menuOpen],
  );

  const {
    sidebarNavigation: { appModules },
  } = useModules();

  const {
    sidebar: { hideSidebarDividers },
  } = getAppFrameTheme(useTheme());

  const sidebarItems = useMemo(() => {
    const normalizedModules = normalizeModules(appModules, { t, exists }, handleRightClick);
    const groupedElements = groupModules(normalizedModules);

    if (groupedElements.size === 1 || hideSidebarDividers) {
      return normalizedModules;
    }

    const result: (ReactNode | SidebarMenuItemEntity)[] = [];
    Array.from(groupModules(normalizedModules)).forEach(([, modulesInGroup], idx) => {
      if (!modulesInGroup.length) {
        return;
      }
      if (idx) {
        result.push(<Divider key={uniqueId('Sidebar-divider_')} />);
      }
      result.push(...modulesInGroup);
    });

    return result;
  }, [appModules, handleRightClick, hideSidebarDividers, t, exists]);

  const handleReorderSave = useCallback(
    async (event: React.MouseEvent<HTMLElement, MouseEvent>, menuItemOrder: MenuItemOrder) => {
      await saveReorderToStorage(menuItemOrder);
    },
    [],
  );

  const handleResetReorder = useCallback(async () => {
    await saveReorderToStorage({});
  }, []);

  const [orderedSidebarItems, setOrderedSidebarItems] = useState<SavedSidebarItem[]>(() =>
    allowReorder ? sortSidebarItems(sidebarItems) : sidebarItems,
  );

  useEffect(() => {
    if (!allowReorder) {
      return undefined;
    }

    setOrderedSidebarItems(sortSidebarItems(sidebarItems));
    const unsubscribe = services.appFrame.userStorage.onDataChange(() => {
      setOrderedSidebarItems(sortSidebarItems(sidebarItems));
    });

    return () => {
      unsubscribe();
    };
  }, [allowReorder, sidebarItems]);

  const handleSidebarOpen = useCallback<SidebarOpenHandler>(({ open }) => {
    setTimeout(() => setMenuOpen(open));
  }, []);

  const displaySubmenuInPopup = getMainConfig('displaySubmenuInPopup');

  const allowFullscreen = getMainConfig('allowFullscreen');
  const isFullscreenEnabled = useFullscreen();
  const handleFullscreenClick = useCallback(() => {
    services.appFrame.viewport.setFullScreen(!isFullscreenEnabled);
  }, [isFullscreenEnabled]);

  const disablePinning = useMemo(() => isSidebarPinningDisabled(sidebarItems), [sidebarItems]);

  return (
    <>
      <SidebarNavigationRoot className={clsx(sidebarNavigationClasses.root, className)}>
        <Sidebar
          data-testid="SidebarNavigation-navigation"
          className={className}
          open={!!menuOpen}
          openWidth={260}
          items={orderedSidebarItems}
          LinkComponent={NavLink}
          showMainItemInPopupMenu
          onOpen={handleSidebarOpen}
          onMenuItemClick={handleMenuItemClick}
          submenuContainer={submenuContainerRef.current}
          disableAutoOpenOnClick={displaySubmenuInPopup ?? false}
          disablePinning={disablePinning}
          ToggleOpenProps={
            {
              'data-testid': `Sidebar-toggleButton--${menuOpen ? 'close' : 'open'}`,
            } as IconButtonProps
          }
          allowReorder={allowReorder}
          onReorderSave={handleReorderSave}
          onResetReorder={handleResetReorder}
          fullscreenEnabled={isFullscreenEnabled}
          onFullscreenClick={allowFullscreen === false ? undefined : handleFullscreenClick}
          FullscreenProps={
            {
              'data-testid': 'Sidebar-fullscreenButton',
            } as MenuItemProps
          }
        />
      </SidebarNavigationRoot>
      {!!contextMenu.anchorEl && (
        <AnchorContextMenu contextMenuOptions={contextMenu} onClose={handleCloseContextMenu} />
      )}
    </>
  );
};

export default SidebarNavigation;
